import React from 'react';
import { graphql } from 'gatsby';
import { HelmetDatoCms } from 'gatsby-source-datocms';
import Layout from '../components/Layout';
import Banner from '../components/Banner';
import FeaturedLogos from '../components/FeaturedLogos';
import FeaturedCategories from '../components/FeaturedCategories';
import CompanyReviews from '../components/CompanyReviews';
import FeaturedProducts from '../components/FeaturedProducts';
import ImageCtas from '../components/ImageCtas';
import FeaturedBlogPosts from '../components/FeaturedBlogPosts';
import Faqs from '../components/Faqs';
import IconCtas from '../components/IconCtas';
import ShopInformation from '../components/ShopInformation';
import { getSchemaDescription } from '../utils';

const HomePageTemplate = ({
  data: {
    datoCmsHome: {
      seoMetaTags,
      title,
      bannerImage,
      bannerHeading,
      bannerLinks,
      productCategoriesHeading,
      productCategoriesText,
      featuredProductsHeading,
      companyReviewsHeading,
      shopInformationHeading,
      shopInformationText,
      shopInformationImage,
      imageCtas,
      faqsHeading,
      faqs,
      faqsLinkPage,
      featuredBlogsHeading,
    },
    allShopifyProduct: { nodes: featuredProducts },
    datoCmsContactInformation: {
      shopLocations,
      instagramProfile,
      facebookProfile,
      linkedinProfile,
    },
  },
}) => {
  const siteUrl = process.env.GATSBY_SITE_URL;

  const schema = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': ['OnlineStore', 'Organization'],
        '@id': `${siteUrl}/#organization`,
        name: 'Root Houseplants',
        url: siteUrl,
        description: getSchemaDescription(seoMetaTags),
        logo: {
          '@type': 'ImageObject',
          '@id': `${siteUrl}/#logo`,
          url: `${siteUrl}/logo.svg`,
        },
        image: {
          '@id': `${siteUrl}/#logo`,
        },
        sameAs: [instagramProfile, facebookProfile, linkedinProfile].filter(
          Boolean
        ),
        location: shopLocations
          .map(location => ({
            '@type': 'Place',
            name: location.name,
            address: {
              '@type': 'PostalAddress',
              streetAddress: location.address,
            },
            description: `Opening hours: ${location.openingHours}`,
          }))
          .filter(Boolean),
      },
      {
        '@type': 'WebSite',
        '@id': `${siteUrl}/#website`,
        url: siteUrl,
        name: 'Root Houseplants',
        publisher: {
          '@id': `${siteUrl}/#organization`,
        },
      },
    ],
  };

  return (
    <>
      <HelmetDatoCms seo={seoMetaTags}>
        <script type="application/ld+json">
          {JSON.stringify(schema, null, 2)}
        </script>
      </HelmetDatoCms>
      <Layout
        title={title}
        description={getSchemaDescription(seoMetaTags)}
        isHomepage={true}
      >
        <main>
          <Banner
            image={bannerImage}
            heading={bannerHeading}
            links={bannerLinks}
            isHomepage={true}
          />
          <FeaturedLogos isHomepage={true} />
          <FeaturedCategories
            heading={productCategoriesHeading}
            text={productCategoriesText}
          />
          <CompanyReviews heading={companyReviewsHeading} />
          <FeaturedProducts
            heading={featuredProductsHeading}
            items={featuredProducts}
          />
          <ImageCtas items={imageCtas} />
          <FeaturedBlogPosts heading={featuredBlogsHeading} />
          <Faqs heading={faqsHeading} items={faqs} link={faqsLinkPage} />
          <IconCtas />
          <ShopInformation
            heading={shopInformationHeading}
            text={shopInformationText}
            image={shopInformationImage}
          />
        </main>
      </Layout>
    </>
  );
};

export const HomePageTemplateQuery = graphql`
  query HomePageTemplateQuery($featuredProducts: [String]) {
    datoCmsHome {
      seoMetaTags {
        ...GatsbyDatoCmsSeoMetaTags
      }
      title
      bannerImage {
        gatsbyImageData(width: 1400, height: 600, layout: FULL_WIDTH)
        alt
      }
      bannerHeading
      bannerLinks {
        id
        text
        page {
          ...LinkFragment
        }
      }
      productCategoriesHeading
      productCategoriesText
      ctaStripHeading
      ctaStripText
      ctaStripLink {
        text
        page {
          ...LinkFragment
        }
      }
      featuredProductsHeading
      companyReviewsHeading
      shopInformationHeading
      shopInformationText
      shopInformationImage {
        url
        alt
      }
      imageCtas {
        id
        heading
        text
        image {
          gatsbyImageData(width: 250, height: 400)
          alt
        }
        link {
          text
          page {
            ...LinkFragment
          }
        }
      }
      faqsHeading
      faqs {
        id
        heading
        text
      }
      faqsLinkPage {
        ...LinkFragment
      }
      featuredBlogsHeading
    }
    allShopifyProduct(filter: { handle: { in: $featuredProducts } }) {
      nodes {
        ...ProductCardFragment
      }
    }
    datoCmsContactInformation {
      instagramProfile
      facebookProfile
      linkedinProfile
      shopLocations {
        name
        address
        openingHours
      }
    }
  }
`;

export default HomePageTemplate;
